import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { supabase } from "../../lib/supabase";
import type { Deal } from "../../lib/types";
import { sendEnrouteText, unscheduleDrone } from "../../lib/droneActions";
import { useAuth } from "../../context/AuthContext";
import CompleteDroneModal from "./actions/CompleteDroneModal";
import UnableToFlyModal from "./actions/UnableToFlyModal";
import DealCallText from "../DealCallText";
import RouteMap, { dealsToRouteStops } from "../shared/RouteMap";
import RouteBuilder from "./RouteBuilder";

import DealLink from "../DealLink";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";

const PIN_COLORS = ["#f46530", "#345c72", "#d4edf4", "#2a2e30", "#ff9e7a"];
function pinColor(idx: number): string {
  return PIN_COLORS[idx % PIN_COLORS.length];
}

const DEAL_COLS =
  "id,homeowner_name,homeowner_phone,homeowner_email,property_street,property_city,property_state,property_zip,property_lat,property_lon,claim_number,stage,phase,appointment_date,appointment_verified,appointment_verified_at,assigned_drone_pilot_id,drone_scheduled_date,drone_completed_date,drone_sla_met,drone_unable_to_fly,drone_unable_reason,needs_climb,imging_report_link,owner_id,split_rm_id,days_in_stage";

function formatAddress(d: Deal) {
  return [
    d.property_street,
    [d.property_city, d.property_state].filter(Boolean).join(", "),
    d.property_zip,
  ]
    .filter(Boolean)
    .join(" ");
}

export default function TodayRouteTab() {
  const { member } = useAuth();
  const today = useMemo(() => new Date(), []);
  const todayISO = format(today, "yyyy-MM-dd");
  const dayStart = `${todayISO}T00:00:00Z`;
  const dayEnd = `${todayISO}T23:59:59Z`;

  const [stops, setStops] = useState<Deal[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const [completeFor, setCompleteFor] = useState<Deal | null>(null);
  const [unableFor, setUnableFor] = useState<Deal | null>(null);
  const [enroutePending, setEnroutePending] = useState<string | null>(null);
  const [rerouteOpen, setRerouteOpen] = useState(false);
  const [unschedulePending, setUnschedulePending] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setError(null);
      const { data, error } = await supabase
        .from("deals")
        .select(DEAL_COLS)
        .eq("stage", "drone_scheduled")
        .gte("drone_scheduled_date", dayStart)
        .lte("drone_scheduled_date", dayEnd)
        .order("drone_scheduled_date", { ascending: true });
      if (error) {
        if (!cancelled) setError(error.message);
        return;
      }
      if (!cancelled) setStops((data ?? []) as Deal[]);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [dayStart, dayEnd, reloadKey]);

  async function handleEnroute(deal: Deal) {
    setEnroutePending(deal.id);
    try {
      await sendEnrouteText(deal, member?.id ?? null);
    } catch (e) {
      alert("Failed to send: " + (e as Error).message);
    } finally {
      setEnroutePending(null);
    }
  }

  async function handleUnschedule(deal: Deal) {
    const confirmed = window.confirm(
      `Send "${deal.homeowner_name ?? "(no name)"}" back to the Appointment Received queue?\n\nThe fly date and pilot assignment will be cleared so you can re-route it on a different day.`,
    );
    if (!confirmed) return;
    const reason =
      window.prompt(
        "Reason (optional — e.g. weather, no-show, ran out of daylight):",
        "Weather",
      ) ?? "";
    setUnschedulePending(deal.id);
    try {
      await unscheduleDrone(deal, { reason }, member?.id ?? null);
      reload();
    } catch (e) {
      alert("Failed to unschedule: " + (e as Error).message);
    } finally {
      setUnschedulePending(null);
    }
  }

  // Build route stops for the map
  const routeStops = useMemo(
    () => (stops ? dealsToRouteStops(stops) : []),
    [stops],
  );

  if (error)
    return (
      <div className="bg-red-50 text-red-700 p-4 rounded">
        Couldn't load today's route — {error}
      </div>
    );
  if (!stops)
    return <div className="text-slate-500 p-4">Loading today's route…</div>;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="text-sm text-slate-600">
          {format(today, "EEEE, MMMM d")} — {stops.length}{" "}
          {stops.length === 1 ? "stop" : "stops"}
        </div>
        {stops.length >= 2 && (
          <button
            onClick={() => setRerouteOpen(true)}
            className="px-3 py-1.5 rounded bg-bardin-orange text-white text-sm font-medium hover:opacity-90 inline-flex items-center gap-1.5"
            title="Re-optimize today's route from a starting point"
          >
            🗺️ Re-route
          </button>
        )}
      </div>

      {/* Route map */}
      <RouteMap stops={routeStops} minutesPerStop={7} perStopLabel="flying" />

      {stops.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-slate-500">
          No drone stops scheduled for today. Pull from the Scheduling Queue
          tab to build a route.
        </div>
      ) : (
        <ul className="space-y-3">
          {stops.map((d, idx) => (
            <li key={d.id} className="bg-white rounded-xl shadow">
              <div className="p-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                <div className="flex gap-3 items-start">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 mt-0.5"
                    style={{ background: pinColor(idx) }}
                  >
                    {idx + 1}
                  </div>
                  <div>
                    {d.claim_number && (
                      <span className="text-xs text-slate-400">
                        #{d.claim_number}
                      </span>
                    )}
                    <div className="font-semibold text-bardin-ink">
                      <DealLink id={d.id}>
                        {d.homeowner_name ?? "(no name)"}
                      </DealLink>
                    </div>
                    <div className="text-sm text-slate-600">
                      {isAddressLinkable(d) ? (
                        <a
                          href={mapsHref(d)}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="hover:underline hover:text-slate-800"
                        >
                          {formatAddress(d)}
                        </a>
                      ) : (
                        formatAddress(d) || "(no address)"
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0 ml-11 md:ml-0">
                  {isAddressLinkable(d) && (
                    <a
                      href={mapsHref(d)}
                      target="_blank"
                      rel="noreferrer"
                      className="px-3 py-1.5 rounded bg-bardin-teal text-white text-sm hover:bg-slate-700"
                    >
                      Navigate
                    </a>
                  )}
                  <DealCallText
                    dealId={d.id}
                    homeownerName={d.homeowner_name}
                    homeownerPhone={d.homeowner_phone}
                    textOnly
                  />
                  <button
                    onClick={() => handleEnroute(d)}
                    disabled={enroutePending === d.id}
                    className="px-3 py-1.5 rounded bg-slate-100 text-slate-700 text-sm hover:bg-slate-200 disabled:opacity-50"
                  >
                    {enroutePending === d.id ? "Sending…" : "En-route text"}
                  </button>
                  <button
                    onClick={() => handleUnschedule(d)}
                    disabled={unschedulePending === d.id}
                    className="px-3 py-1.5 rounded bg-slate-100 text-slate-700 text-sm hover:bg-slate-200 disabled:opacity-50"
                    title="Send this stop back to the Appointment Received queue (e.g. rain pushed it to another day)"
                  >
                    {unschedulePending === d.id ? "Sending…" : "Back to queue"}
                  </button>
                  <button
                    onClick={() => setUnableFor(d)}
                    className="px-3 py-1.5 rounded bg-amber-100 text-amber-800 text-sm hover:bg-amber-200"
                  >
                    Unable to fly
                  </button>
                  <button
                    onClick={() => setCompleteFor(d)}
                    className="px-3 py-1.5 rounded bg-bardin-orange text-white text-sm hover:opacity-90"
                  >
                    Complete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {completeFor && (
        <CompleteDroneModal
          open
          deal={completeFor}
          onClose={() => setCompleteFor(null)}
          onCompleted={reload}
        />
      )}
      {unableFor && (
        <UnableToFlyModal
          open
          deal={unableFor}
          onClose={() => setUnableFor(null)}
          onCompleted={reload}
        />
      )}
      {rerouteOpen && (
        <RouteBuilder
          deals={stops}
          mode="reroute"
          title={`Re-route — ${format(today, "MMM d")}`}
          onClose={() => setRerouteOpen(false)}
        />
      )}
    </div>
  );
}
